import { NetworkStatus, useQuery } from "@apollo/client";
import {
  AssetCard,
  Button,
  ControlledDateFilterControl,
  ControlledDateFilterSummary,
  ControlledFacetedFilterControl,
  ControlledFactedFilterSummary,
  CurrencyAmountUI,
  DataTable,
  DataTableColumnHeader,
  DateWithDelta,
  type FacetedFilterOption,
  HoverLink,
  Popover,
  PopoverContent,
  PopoverTrigger,
  PrimaryFullScreenSpinner,
  StatusBase,
  StatusUI,
  useQueryPersistedStateWithSchema,
  useRouter,
} from "@repo/ui";
import { currencySchema, isCurrency } from "@repo/utils";
import {
  createFileRoute,
  Link,
  useNavigate,
  useParams,
} from "@tanstack/react-router";
import type { ColumnDef, SortingState } from "@tanstack/react-table";
import { Flag01 } from "@untitledui/icons";
import { X } from "lucide-react";
import type { ReactNode } from "react";
import { Suspense, useState } from "react";
import {
  RiAddLine,
  RiBuilding2Line,
  RiCalendarLine,
  RiFilter3Line,
  RiMoneyDollarCircleLine,
  RiPriceTagLine,
} from "react-icons/ri";
import { z } from "zod";
import { AdminPageContainer } from "~/components/layout/AdminPageContainer";
import { AdminPageHeader } from "~/components/layout/AdminPageHeader";
import { graphql } from "~/gql/client";
import {
  payoutStatusValues,
  payoutsPageOrderValues,
} from "~/gql/client/enumValues";
import type {
  AdminPayoutRowFragment,
  PayoutsFilter,
  PayoutsPageOrder,
} from "~/gql/client/graphql";
import { opsHead } from "~/head";

export const Route = createFileRoute("/__auth/payouts")({
  head: () => opsHead("Payouts"),
  component: () => (
    <Suspense fallback={<PrimaryFullScreenSpinner />}>
      <View />
    </Suspense>
  ),
});

const columns: ColumnDef<AdminPayoutRowFragment & { id: string }>[] = [
  {
    id: "executionDate",
    accessorFn: (row) => row.requestedForDate,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Date" />
    ),
    cell: ({ row }) => {
      return <DateWithDelta date={row.original.requestedForDate} />;
    },
  },
  {
    id: "organisationId",
    accessorFn: (row) => row.organisation?.id,
  },
  {
    id: "sourceAccount",
    accessorFn: (row) => row.sourceAsset?.name,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Source Account" />
    ),
    cell: ({ row }) => (
      <div>
        {row.original.sourceAsset ? (
          <HoverLink to={`/assets/${row.original.sourceAsset?.id}`}>
            <AssetCard
              asset={row.original.sourceAsset}
              subText={["BUSINESS_ENTITY", "PROVIDER"]}
            />
          </HoverLink>
        ) : (
          "-"
        )}
      </div>
    ),
  },
  {
    id: "beneficiaryAccount",
    accessorFn: (row) => row.beneficiaryAccount?.accountName,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Destination Account" />
    ),
    cell: ({ row }) =>
      row.original.beneficiaryAccount ? (
        <div>
          <Link
            to={`/beneficiary-accounts/${row.original.beneficiaryAccount.id}`}
          >
            <AssetCard
              beneficiaryAccount={row.original.beneficiaryAccount}
              subText={["IDENTIFIER", "CURRENCY"]}
            />
          </Link>
        </div>
      ) : (
        "-"
      ),
  },
  {
    id: "amount",
    accessorFn: (row) => row.amount.amount,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Amount" align="right" />
    ),
    cell: ({ row }) => {
      const { currency, amount } = row.original.amount;

      return (
        <div className="text-right">
          <CurrencyAmountUI {...{ currency, amount }} />
        </div>
      );
    },
  },
  {
    id: "status",
    accessorFn: (row) => row.status,
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      return row.original.status ? (
        <div className="flex flex-col items-start justify-center gap-1">
          <StatusUI status={row.original.status} className="flex-shrink" />
          {row.original.status === "COMPLETED" &&
            !row.original.transactions?.length && (
              <StatusBase label="No Transaction" color="error" icon={Flag01} />
            )}
        </div>
      ) : (
        "-"
      );
    },
  },
];

function View() {
  const params = useParams({ strict: false });
  const navigate = useNavigate();
  const { useSearchParams } = useRouter();

  const [filterAndSortState, setFilterAndSortOrder] =
    useQueryPersistedStateWithSchema({
      useSearchParams,
      schema: z.object({
        businessEntityIds: z.array(z.string()).optional().nullable(),
        organisationIds: z.array(z.string()).optional().nullable(),
        endDateTimeExclusive: z.date().optional().nullable(),
        beneficiaryAccountIds: z.array(z.string()).optional().nullable(),
        beneficiaryAccountCurrencies: z
          .array(currencySchema)
          .optional()
          .nullable(),
        startDateTimeInclusive: z.date().optional().nullable(),
        statuses: z.array(z.enum(payoutStatusValues)).optional().nullable(),
        sortOrder: z.enum(payoutsPageOrderValues).optional().nullable(),
      }),
      defaultState: {
        sortOrder: "DATE_DESC",
      },
    });

  const { sortOrder, ...filter } = filterAndSortState;

  const baseInput = {
    first: 20,
    order: sortOrder,
    filter,
  };

  const { fetchMore, loading, networkStatus, data } = useQuery(
    graphql(`
    query AdminPayouts(
      $after: String,
      $first: Int!,
      $order: PayoutsPageOrder,
      $filter: PayoutsFilter!,
    ) {
      payouts(
        after: $after,
        first: $first,
        order: $order,
        filter: $filter,
      ) {
        pageInfo {
          endCursor
          hasNextPage
        }
        payouts {
            ...AdminPayoutRow
        }
      }
    }

    fragment AdminPayoutRow on Payout {
      id
      requestedForDate
      amount {
        currency
        amount
      }
      status
      sourceAsset {
        ...AssetCardFields
      }
      beneficiaryAccount {
          ...BeneficiaryAssetCardFields
      }
      organisation {
        id
        name
      }
      transactions {
          id
      }
    }
  `),
    {
      variables: {
        after: null,
        ...baseInput,
      },
      notifyOnNetworkStatusChange: true,
    },
  );

  const payouts = data?.payouts?.payouts ?? [];
  const nextCursor = data?.payouts?.pageInfo.hasNextPage
    ? (data.payouts.pageInfo.endCursor ?? undefined)
    : undefined;

  const handleSortChange = (newSortingState: SortingState) => {
    const primarySort = newSortingState.at(0);
    let newSortOrder: PayoutsPageOrder;
    if (primarySort?.id === "executionDate") {
      newSortOrder = primarySort.desc ? "DATE_DESC" : "DATE_ASC";
    } else {
      newSortOrder = "DATE_DESC";
    }
    if (newSortOrder !== sortOrder) {
      setFilterAndSortOrder({
        ...filterAndSortState,
        sortOrder: newSortOrder,
      });
    }
  };

  const { data: pageData } = useQuery(
    graphql(`
    query AdminPayoutsPage {
      organisations {
        id
        name
        businessEntities {
            id
            name
            beneficiaryAccounts(includeInactive: true) {
                ...BeneficiaryAssetCardFields
            }
        }
      }
    }
    `),
  );

  const organisationOptions: FacetedFilterOption[] = (
    pageData?.organisations?.slice() ?? []
  )
    .sort((a, b) => a.name.localeCompare(b.name))
    .map((o) => ({
      value: o.id,
      searchValues: [o.name],
      label: <div>{o.name}</div>,
    }));

  const beneficiaryAccounts =
    pageData?.organisations
      ?.flatMap(
        (o) =>
          o.businessEntities?.flatMap((be) => be?.beneficiaryAccounts ?? []) ??
          [],
      )
      .sort((a, b) => a.displayName.localeCompare(b.displayName)) ?? [];

  const beneficiaryAccountOptions: {
    value: string;
    label: ReactNode;
  }[] = beneficiaryAccounts.map((ba) => ({
    value: ba.id,
    label: <AssetCard beneficiaryAccount={ba} variant="INLINE_TEXT" />,
  }));

  const beneficiaryAccountCurrencyOptions: {
    value: string;
    label: ReactNode;
  }[] = Array.from(new Set(beneficiaryAccounts.map((ba) => ba.currency)))
    .map((c) => ({ label: c, value: c }))
    .sort((a, b) => a.label.localeCompare(b.label));

  return (
    <AdminPageContainer testId="admin-payouts">
      <AdminPageHeader
        title="Payouts"
        subtitle="Withdrawals from Primary-managed assets to external accounts."
        buttons={
          <div className="flex gap-2">
            <Link to={`/payouts/new`}>
              <Button leftIcon={<RiAddLine />}>New payout</Button>
            </Link>
          </div>
        }
      />
      <div>
        <DataTable
          data={payouts}
          columns={columns}
          activeRowId={params.payoutId}
          isLoadingData={loading || networkStatus === NetworkStatus.fetchMore}
          manualSorting={true}
          manualPagination={true}
          maxMultiSortColCount={1}
          hidePagination={true}
          pageSize={10_000}
          onRowClick={(row) => {
            const active = row.original.id === params.payoutId;
            return active
              ? navigate({ to: "/payouts" })
              : navigate({
                  to: "/payouts/$payoutId",
                  params: { payoutId: row.original.id },
                });
          }}
          initialColumnVisibility={{
            "beneficiaryAccount.businessEntity": false,
            "beneficiaryAccount.currency": false,
            organisationId: false,
          }}
          onSortingChange={handleSortChange}
          toolbarComponent={() => (
            <FilterBar
              filterValue={filterAndSortState}
              organisationOptions={organisationOptions}
              onFilterChange={setFilterAndSortOrder}
              beneficiaryAccountCurrencyOptions={
                beneficiaryAccountCurrencyOptions
              }
              beneficiaryAccountOptions={beneficiaryAccountOptions}
            />
          )}
          className="overflow-hidden border"
        />
        <div className="mt-4 flex w-full flex-row justify-center">
          {nextCursor ? (
            <Button
              onClick={() =>
                fetchMore({ variables: { ...baseInput, after: nextCursor } })
              }
              variant="outline"
              loading={loading || networkStatus === NetworkStatus.fetchMore}
            >
              Load more
            </Button>
          ) : (
            !!payouts.length && (
              <p className="text-secondary text-sm">No more data</p>
            )
          )}
        </div>
      </div>
    </AdminPageContainer>
  );
}

function FilterBar({
  filterValue,
  onFilterChange,
  organisationOptions,
  beneficiaryAccountOptions,
  beneficiaryAccountCurrencyOptions,
}: {
  filterValue: PayoutsFilter;
  onFilterChange: (_: PayoutsFilter) => void;
  organisationOptions: FacetedFilterOption[];
  beneficiaryAccountOptions: FacetedFilterOption[];
  beneficiaryAccountCurrencyOptions: FacetedFilterOption[];
}) {
  const [open, setOpen] = useState(false);
  const [path, setPath] = useState<string>("date");
  let filterCount = 0;
  if ((filterValue?.organisationIds?.length ?? 0) > 0) {
    filterCount++;
  }
  if (filterValue.startDateTimeInclusive || filterValue.endDateTimeExclusive) {
    filterCount++;
  }
  if ((filterValue?.beneficiaryAccountIds?.length ?? 0) > 0) {
    filterCount++;
  }
  if ((filterValue?.beneficiaryAccountCurrencies?.length ?? 0) > 0) {
    filterCount++;
  }

  const resetFilter = () => {
    onFilterChange({
      ...filterValue,
      startDateTimeInclusive: undefined,
      endDateTimeExclusive: undefined,
      organisationIds: undefined,
      statuses: undefined,
      beneficiaryAccountIds: undefined,
      beneficiaryAccountCurrencies: undefined,
    });
  };

  const statusOptions = payoutStatusValues.map((k) => ({
    value: k,
    label: <span>{k}</span>,
  }));

  return (
    <div className="flex w-full flex-row gap-2">
      <Popover open={open} onOpenChange={(open) => setOpen(open)}>
        <PopoverTrigger asChild>
          <Button
            leftIcon={<RiFilter3Line />}
            variant="outline"
            size="sm"
            className="mb-4 h-8 w-fit text-primary"
          >
            Filter{" "}
            {filterCount > 0 && (
              <span className="ml-2">
                <span className="text-info">({filterCount})</span>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="flex h-[500px] w-[760px] p-0 " align="start">
          <div className="flex w-[260px] flex-col justify-between rounded-l-md border-r bg-neutral-muted p-6">
            <div className="flex flex-col gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("date")}
              >
                <RiCalendarLine />
                <div className="flex flex-1">Date</div>
                {(filterValue.endDateTimeExclusive ||
                  filterValue.startDateTimeInclusive) && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("status")}
              >
                <RiPriceTagLine />
                <div className="flex flex-1">Status</div>
                {(filterValue.statuses?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("organisation")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Organisation</div>
                {(filterValue.organisationIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("beneficiaryAccount")}
              >
                <RiBuilding2Line />
                <div className="flex flex-1">Beneficiary Account</div>
                {(filterValue.beneficiaryAccountIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="flex justify-start gap-2 text-primary"
                onClick={() => setPath("currency")}
              >
                <RiMoneyDollarCircleLine />
                <div className="flex flex-1">Currency</div>
                {(filterValue.beneficiaryAccountIds?.length ?? 0) > 0 && (
                  <div className="size-2.5 rounded-full border-2 border-info bg-info text-xs" />
                )}
              </Button>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="h-8 bg-primary text-primary"
              onClick={resetFilter}
            >
              Reset all filters
            </Button>
          </div>
          <div className="flex-1">
            {path === "date" && (
              <ControlledDateFilterControl
                onChange={(value) => {
                  onFilterChange({
                    ...filterValue,
                    startDateTimeInclusive: value?.startAtInclusive,
                    endDateTimeExclusive: value?.endAtExclusive,
                  });
                }}
                filterValue={{
                  startAtInclusive:
                    filterValue.startDateTimeInclusive ?? undefined,
                  endAtExclusive: filterValue.endDateTimeExclusive ?? undefined,
                }}
              />
            )}
            {path === "statuses" && (
              <ControlledFacetedFilterControl
                options={statusOptions}
                selectedValues={filterValue.statuses ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    statuses: newSelectedValues.length
                      ? newSelectedValues
                      : undefined,
                  });
                }}
              />
            )}
            {path === "organisation" && (
              <ControlledFacetedFilterControl
                options={organisationOptions}
                selectedValues={filterValue.organisationIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    organisationIds: newSelectedValues.length
                      ? newSelectedValues.filter((v) => v !== null)
                      : undefined,
                  });
                }}
              />
            )}
            {path === "beneficiaryAccount" && (
              <ControlledFacetedFilterControl
                options={beneficiaryAccountOptions}
                selectedValues={filterValue.beneficiaryAccountIds ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    beneficiaryAccountIds: newSelectedValues.length
                      ? newSelectedValues.filter((v) => v !== null)
                      : undefined,
                  });
                }}
              />
            )}
            {path === "currency" && (
              <ControlledFacetedFilterControl
                options={beneficiaryAccountCurrencyOptions}
                selectedValues={filterValue.beneficiaryAccountCurrencies ?? []}
                onChange={(newSelectedValues) => {
                  onFilterChange({
                    ...filterValue,
                    beneficiaryAccountCurrencies: newSelectedValues.length
                      ? newSelectedValues.filter(
                          (s) => s !== null && isCurrency(s),
                        )
                      : undefined,
                  });
                }}
              />
            )}
          </div>
        </PopoverContent>
      </Popover>

      <ControlledDateFilterSummary
        onClick={() => {
          setPath("date");
          setOpen(true);
        }}
        value={{
          startAtInclusive: filterValue.startDateTimeInclusive ?? undefined,
          endAtExclusive: filterValue.endDateTimeExclusive ?? undefined,
        }}
      />

      <ControlledFactedFilterSummary
        title="Status"
        options={statusOptions}
        selectedValues={filterValue.statuses ?? []}
        onClick={() => {
          setPath("statuses");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Organisation"
        options={organisationOptions}
        selectedValues={filterValue.organisationIds ?? []}
        onClick={() => {
          setPath("organisation");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Beneficiary Account"
        options={beneficiaryAccountOptions}
        selectedValues={filterValue.beneficiaryAccountIds ?? []}
        onClick={() => {
          setPath("beneficiaryAccount");
          setOpen(true);
        }}
      />

      <ControlledFactedFilterSummary
        title="Currency"
        options={beneficiaryAccountCurrencyOptions}
        selectedValues={filterValue.beneficiaryAccountCurrencies ?? []}
        onClick={() => {
          setPath("currency");
          setOpen(true);
        }}
      />

      {filterCount > 0 && (
        <Button
          rightIcon={<X />}
          variant="ghost"
          size="sm"
          onClick={resetFilter}
        >
          Reset
        </Button>
      )}
    </div>
  );
}
